import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import HeaderTwo from '../common/header/HeaderTwo';
import FooterOne from '../common/footer/FooterOne';
import { MapView, ListingCard } from '../components/directory';
import { getListingBySlug, getListingsByType, listings } from '../data';
import linkingHelper from '../utils/linkingHelper';
import {
  MapPin, Phone, Mail, Globe, Clock, Star, ChevronLeft,
  Heart, Share2, Navigation, Calendar, Check, X, Leaf, Truck, Users,
  Search, Dog, Sprout, Home, Tractor, ShoppingBasket, Monitor, CreditCard, Accessibility, Bath, ExternalLink, Building2
} from 'lucide-react';
import {
  NutritionInfoWidget,
  SeasonalProduceWidget,
  RecipeSuggestionsWidget,
  FarmRecommendationsWidget
} from '../components/widgets';

const ListingsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  // Get listing by slug (id param is actually the slug)
  const listing = getListingBySlug(id);

  // If listing not found, show error
  if (!listing) {
    return (
      <>
        <HeaderTwo />
        <div style={styles.errorContainer}>
          <div className="container">
            <div style={styles.errorContent}>
              <span style={styles.errorIcon}><Search size={48} /></span>
              <h1 style={styles.errorTitle}>Listing Not Found</h1>
              <p style={styles.errorText}>
                The listing you're looking for doesn't exist or has been removed.
              </p>
              <Link to="/browse" style={styles.backBtn}>
                <ChevronLeft size={18} />
                Back to Listings
              </Link>
            </div>
          </div>
        </div>
        <FooterOne />
      </>
    );
  }

  // Get similar listings (same type, excluding current)
  const similarListings = getListingsByType(listing.type)
    .filter(l => l.id !== listing.id)
    .slice(0, 3);

  // Format hours for display
  const formatHours = (hours) => {
    if (!hours) return null;
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return days.map(day => ({
      day: day.charAt(0).toUpperCase() + day.slice(1),
      hours: hours[day] || 'Closed',
    }));
  };

  const formattedHours = formatHours(listing.hours);

  const renderSeasonalAvailability = (seasonalAvailability) => {
    if (!seasonalAvailability) return null;
    if (typeof seasonalAvailability === 'string') return seasonalAvailability;

    if (typeof seasonalAvailability === 'object') {
      const orderedSeasons = ['spring', 'summer', 'fall', 'winter'];
      const parts = [];

      for (const season of orderedSeasons) {
        const value = seasonalAvailability[season];
        if (!value) continue;

        const label = season.charAt(0).toUpperCase() + season.slice(1);
        const items = Array.isArray(value) ? value.join(', ') : String(value);
        parts.push(`${label}: ${items}`);
      }

      return parts.length ? parts.join(' • ') : null;
    }

    return String(seasonalAvailability);
  };

  // Check if currently open (simplified logic)
  const isOpen = () => {
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const todayHours = listing.hours?.[currentDay];
    return todayHours && todayHours !== 'Closed';
  };

  // Feature icons mapping - using Lucide React icons
  const featureIcons = {
    'Pet Friendly': Dog,
    'Organic': Sprout,
    'Family Owned': Home,
    'Farm Tours': Tractor,
    'U-Pick Available': ShoppingBasket,
    'Delivery': Truck,
    'Online Ordering': Monitor,
    'Credit Cards Accepted': CreditCard,
    'Wheelchair Accessible': Accessibility,
    'Restrooms': Bath,
  };

  const placeholderImage = '/images/placeholders/produce-fallback.svg';
  const listingImages = Array.isArray(listing.images) && listing.images.length > 0
    ? listing.images
    : (listing.logo ? [listing.logo] : [placeholderImage]);

  return (
    <>
      <HeaderTwo />

      {/* Hero Section */}
      <section style={styles.heroSection}>
        <div className="container">
          <Link to="/browse" style={styles.breadcrumbLink}>
            <ChevronLeft size={16} />
            Back to Listings
          </Link>
        </div>

        {/* Image Gallery */}
        <div style={styles.imageGallery}>
          <div style={styles.mainImage}>
            <img
              src={listingImages[activeImageIndex] || placeholderImage}
              alt={listing.name}
              style={styles.mainImg}
              onError={(e) => {
                e.target.src = placeholderImage;
              }}
            />
            <div style={styles.imageOverlay}>
              <span style={styles.listingType}>{listing.type}</span>
              <div style={styles.imageActions}>
                <button
                  style={{ ...styles.actionBtn, ...(isFavorite ? styles.actionBtnActive : {}) }}
                  onClick={() => setIsFavorite(!isFavorite)}
                >
                  <Heart size={20} fill={isFavorite ? '#fff' : 'none'} />
                </button>
                <button style={styles.actionBtn}>
                  <Share2 size={20} />
                </button>
              </div>
            </div>
          </div>
          {listingImages.length > 1 && (
            <div style={styles.thumbnailRow}>
              {listingImages.slice(0, 4).map((img, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.thumbnail,
                    ...(index === activeImageIndex ? styles.thumbnailActive : {}),
                  }}
                  onClick={() => setActiveImageIndex(index)}
                >
                  <img
                    src={img}
                    alt={`${listing.name} ${index + 1}`}
                    style={styles.thumbnailImg}
                    onError={(e) => {
                      e.target.src = placeholderImage;
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section style={styles.mainContent}>
        <div className="container">
          <div className="row">
            {/* Left Column - Details */}
            <div className="col-lg-8 col-12">
              {/* Header Info */}
              <div style={styles.listingHeader}>
                <div style={styles.headerMain}>
                  <h1 style={styles.listingName}>{listing.name}</h1>
                  <div style={styles.ratingRow}>
                    <div style={styles.rating}>
                      <Star size={18} fill="var(--thm-accent)" color="var(--thm-accent)" />
                      <span style={styles.ratingScore}>{listing.rating}</span>
                      <span style={styles.reviewCount}>({listing.reviewCount} reviews)</span>
                    </div>
                    <span style={{
                      ...styles.statusBadge,
                      ...(isOpen() ? styles.statusOpen : styles.statusClosed),
                    }}>
                      {isOpen() ? 'Open Now' : 'Closed'}
                    </span>
                  </div>
                  <p style={styles.location}>
                    <MapPin size={16} />
                    {listing.address}, {listing.city}, {listing.state} {listing.zip}
                  </p>
                </div>
              </div>

              {/* Tabs */}
              <div style={styles.tabsContainer}>
                <div style={styles.tabs}>
                  {['overview', 'products', 'hours', 'reviews'].map(tab => (
                    <button
                      key={tab}
                      style={{
                        ...styles.tab,
                        ...(activeTab === tab ? styles.tabActive : {}),
                      }}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div style={styles.tabContent}>
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div>
                      <h3 style={styles.sectionTitle}>About</h3>
                      <p style={styles.description}>{listing.description}</p>

                      {listing.tags && listing.tags.length > 0 && (
                        <div style={styles.featuresSection}>
                          <h3 style={styles.sectionTitle}>Features & Amenities</h3>
                          <div style={styles.featuresList}>
                            {listing.tags.map((tag, index) => {
                              const FeatureIcon = featureIcons[tag];
                              return (
                                <div key={index} style={styles.featureItem}>
                                  <span style={styles.featureIcon}>
                                    {FeatureIcon ? <FeatureIcon size={16} /> : <Check size={16} />}
                                  </span>
                                  <span style={styles.featureName}>{tag}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {listing.seasonalAvailability && (
                        <div style={styles.seasonalSection}>
                          <h3 style={styles.sectionTitle}>Seasonal Availability</h3>
                          <p style={styles.seasonalText}>
                            <Calendar size={16} style={{ marginRight: '8px' }} />
                            {renderSeasonalAvailability(listing.seasonalAvailability)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Products Tab */}
                  {activeTab === 'products' && (
                    <div>
                      <h3 style={styles.sectionTitle}>Products Available</h3>

                      {/* Nutrition Info Widget - Show for first product */}
                      {(listing.products?.vegetables?.[0]?.name ||
                        listing.products?.fruit?.[0]?.name ||
                        listing.productTypes?.[0]) && (
                        <div style={{ marginBottom: '24px' }}>
                          <NutritionInfoWidget
                            produceName={
                              listing.products?.vegetables?.[0]?.name ||
                              listing.products?.vegetables?.[0] ||
                              listing.products?.fruit?.[0]?.name ||
                              listing.products?.fruit?.[0] ||
                              listing.productTypes?.[0]
                            }
                            showHealthScore={true}
                          />
                        </div>
                      )}

                      {/* Handle nested products structure (farms) */}
                      {listing.products && typeof listing.products === 'object' && !Array.isArray(listing.products) && (
                        <div>
                          {listing.products.categories && (
                            <div style={{ marginBottom: '20px' }}>
                              <p style={{ color: '#5D6B5E', marginBottom: '8px' }}>
                                <strong>Total Varieties:</strong> {listing.products.total_varieties || 'Multiple varieties available'}
                              </p>
                            </div>
                          )}

                          {/* Vegetables */}
                          {listing.products.vegetables && listing.products.vegetables.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ ...styles.subsectionTitle, color: '#2D5A27' }}>Vegetables</h4>
                              <div style={styles.productsList}>
                                {listing.products.vegetables.map((item, index) => {
                                  const itemName = item.name || item;
                                  const productUrl = linkingHelper.getProductUrl(itemName);
                                  return (
                                    <Link
                                      key={index}
                                      to={productUrl}
                                      style={styles.productTagLink}
                                      title={`Find ${itemName} farms`}
                                    >
                                      <span style={styles.productTag}>
                                        {itemName}
                                        {item.varieties && ` (${item.varieties.length} varieties)`}
                                        <ExternalLink size={12} style={{ marginLeft: '6px', opacity: 0.7 }} />
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Herbs */}
                          {listing.products.herbs && listing.products.herbs.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ ...styles.subsectionTitle, color: '#4A7C43' }}>Herbs</h4>
                              <div style={styles.productsList}>
                                {listing.products.herbs.map((item, index) => {
                                  const itemName = item.name || item;
                                  const productUrl = linkingHelper.getProductUrl(itemName);
                                  return (
                                    <Link
                                      key={index}
                                      to={productUrl}
                                      style={styles.productTagLink}
                                      title={`Find ${itemName} farms`}
                                    >
                                      <span style={styles.productTag}>
                                        {itemName}
                                        <ExternalLink size={12} style={{ marginLeft: '6px', opacity: 0.7 }} />
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Fruits */}
                          {listing.products.fruit && listing.products.fruit.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ ...styles.subsectionTitle, color: '#C4704F' }}>Fruits</h4>
                              <div style={styles.productsList}>
                                {listing.products.fruit.map((item, index) => {
                                  const itemName = item.name || item;
                                  const productUrl = linkingHelper.getProductUrl(itemName);
                                  return (
                                    <Link
                                      key={index}
                                      to={productUrl}
                                      style={styles.productTagLink}
                                      title={`Find ${itemName} farms`}
                                    >
                                      <span style={styles.productTag}>
                                        {itemName}
                                        <ExternalLink size={12} style={{ marginLeft: '6px', opacity: 0.7 }} />
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Flowers */}
                          {listing.products.flowers && listing.products.flowers.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ ...styles.subsectionTitle, color: '#D4883A' }}>Flowers</h4>
                              <div style={styles.productsList}>
                                {listing.products.flowers.map((item, index) => (
                                  <span key={index} style={styles.productTag}>
                                    {item.name || item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Specialty Items */}
                          {listing.products.specialty_items && listing.products.specialty_items.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ ...styles.subsectionTitle, color: '#6B8E23' }}>Specialty Items</h4>
                              <div style={styles.productsList}>
                                {listing.products.specialty_items.map((item, index) => (
                                  <span key={index} style={styles.productTag}>
                                    {item.name || item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Handle flat array structure (markets - productTypes) */}
                      {listing.productTypes && Array.isArray(listing.productTypes) && (
                        <div style={styles.productsList}>
                          {listing.productTypes.map((product, index) => {
                            const productUrl = linkingHelper.getProductUrl(product);
                            return (
                              <Link
                                key={index}
                                to={productUrl}
                                style={styles.productTagLink}
                                title={`Find ${product} farms`}
                              >
                                <span style={styles.productTag}>
                                  {product}
                                  <ExternalLink size={12} style={{ marginLeft: '6px', opacity: 0.7 }} />
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      )}

                      {/* Handle flat products array (legacy format) */}
                      {listing.products && Array.isArray(listing.products) && (
                        <div style={styles.productsList}>
                          {listing.products.map((product, index) => (
                            <span key={index} style={styles.productTag}>
                              {product}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* No products available message */}
                      {!listing.products && !listing.productTypes && (
                        <p style={styles.noDataText}>
                          Product information is being updated. Please contact the vendor directly for current availability.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Hours Tab */}
                  {activeTab === 'hours' && (
                    <div>
                      <h3 style={styles.sectionTitle}>Business Hours</h3>
                      {formattedHours ? (
                        <div style={styles.hoursList}>
                          {formattedHours.map(({ day, hours }, index) => {
                            // formatHours returns days starting with Monday (index 0)
                            // new Date().getDay() returns 0 for Sunday, 1 for Monday, etc.
                            // Convert getDay() to match formatHours index: (getDay() + 6) % 7
                            const todayIndex = (new Date().getDay() + 6) % 7;
                            const isToday = index === todayIndex;

                            return (
                              <div
                                key={index}
                                style={{
                                  ...styles.hoursRow,
                                  ...(isToday ? styles.hoursRowToday : {}),
                                }}
                              >
                                <span style={{
                                  ...styles.dayName,
                                  ...(isToday ? { fontWeight: '700', color: 'var(--thm-base)' } : {}),
                                }}>
                                  {day}
                                  {isToday && <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--thm-accent)' }}>(Today)</span>}
                                </span>
                                <span style={{
                                  ...styles.hoursText,
                                  ...(isToday ? { fontWeight: '600', color: 'var(--thm-base)' } : {}),
                                }}>
                                  {hours}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p style={styles.noDataText}>Hours not available. Please contact for details.</p>
                      )}
                    </div>
                  )}

                  {/* Reviews Tab */}
                  {activeTab === 'reviews' && (
                    <div>
                      <h3 style={styles.sectionTitle}>Customer Reviews & Ratings</h3>

                      {listing.rating && listing.reviewCount ? (
                        <>
                          <div style={styles.reviewsOverview}>
                            <div style={styles.reviewsScore}>
                              <span style={styles.bigScore}>{listing.rating.toFixed(1)}</span>
                              <div style={styles.starsRow}>
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    size={20}
                                    fill={i < Math.floor(listing.rating) ? 'var(--thm-accent)' : 'none'}
                                    color="var(--thm-accent)"
                                  />
                                ))}
                              </div>
                              <span style={styles.totalReviews}>Based on {listing.reviewCount} reviews</span>
                            </div>
                          </div>

                          <div style={styles.reviewsMessage}>
                            <h4 style={{ ...styles.subsectionTitle, marginTop: '24px' }}>Share Your Experience</h4>
                            <p style={styles.reviewsText}>
                              We value authentic customer experiences! Reviews help our community discover the best local farms and markets in Southwest Florida.
                            </p>
                            {listing.website && (
                              <p style={styles.reviewsText}>
                                Visit <a href={listing.website} target="_blank" rel="noopener noreferrer" style={styles.externalLink}>{listing.name}</a> to learn more about this vendor, or check their reviews on popular platforms:
                              </p>
                            )}
                            <div style={styles.reviewPlatforms}>
                              {listing.name && (
                                <>
                                  <a
                                    href={`https://www.google.com/search?q=${encodeURIComponent(listing.name + ' ' + listing.city + ' reviews')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.platformBtn}
                                  >
                                    Google Reviews
                                  </a>
                                  <a
                                    href={`https://www.yelp.com/search?find_desc=${encodeURIComponent(listing.name)}&find_loc=${encodeURIComponent(listing.city + ', FL')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.platformBtn}
                                  >
                                    Yelp
                                  </a>
                                  <a
                                    href={`https://www.facebook.com/search/top?q=${encodeURIComponent(listing.name + ' ' + listing.city)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.platformBtn}
                                  >
                                    Facebook
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={styles.reviewsMessage}>
                          <p style={styles.reviewsText}>
                            This listing doesn't have ratings yet. If you've visited <strong>{listing.name}</strong>, consider leaving a review on Google, Yelp, or their social media pages to help others in the community!
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Map Section */}
              <div style={styles.mapSection}>
                <h3 style={styles.sectionTitle}>Location</h3>
                <div style={styles.mapContainer}>
                  <MapView
                    listings={[listing]}
                    selectedListing={listing}
                    fullScreen={false}
                  />
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${listing.coordinates.lat},${listing.coordinates.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.directionsBtn}
                >
                  <Navigation size={18} />
                  Get Directions
                </a>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="col-lg-4 col-12">
              <div style={styles.sidebar}>
                {/* Contact Card */}
                <div style={styles.contactCard}>
                  <h3 style={styles.cardTitle}>Contact Information</h3>

                  {listing.phone && (
                    <a href={`tel:${listing.phone}`} style={styles.contactItem}>
                      <Phone size={18} style={styles.contactIcon} />
                      <span>{listing.phone}</span>
                    </a>
                  )}

                  {listing.email && (
                    <a href={`mailto:${listing.email}`} style={styles.contactItem}>
                      <Mail size={18} style={styles.contactIcon} />
                      <span>{listing.email}</span>
                    </a>
                  )}

                  {listing.website && (
                    <a href={listing.website} target="_blank" rel="noopener noreferrer" style={styles.contactItem}>
                      <Globe size={18} style={styles.contactIcon} />
                      <span>Visit Website</span>
                    </a>
                  )}

                  {/* Google Business Profile Link */}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.name + ' ' + listing.address + ' ' + listing.city + ' ' + listing.state)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.contactItem}
                    title="View on Google Maps & Business Profile"
                  >
                    <Building2 size={18} style={styles.contactIcon} />
                    <span>Google Business Profile</span>
                  </a>

                  <div style={styles.contactItem}>
                    <MapPin size={18} style={styles.contactIcon} />
                    <span>{listing.city}, {listing.state}</span>
                  </div>
                </div>

                {/* Quick Hours */}
                <div style={styles.hoursCard}>
                  <h3 style={styles.cardTitle}>
                    <Clock size={18} style={{ marginRight: '8px' }} />
                    Hours Today
                  </h3>
                  <p style={styles.todayHours}>
                    {listing.hours?.[['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][new Date().getDay()]] || 'Contact for hours'}
                  </p>
                </div>

                {/* Seasonal Produce Widget */}
                <SeasonalProduceWidget showPeakOnly={true} maxItems={4} />

                {/* Recipe Suggestions - show if farm has vegetables or fruits */}
                {(listing.products?.vegetables?.length > 0 ||
                  listing.products?.fruit?.length > 0 ||
                  listing.productTypes) && (
                  <RecipeSuggestionsWidget
                    ingredients={[
                      ...(listing.products?.vegetables?.map(v => v.name || v) || []),
                      ...(listing.products?.fruit?.map(f => f.name || f) || []),
                      ...(listing.productTypes || [])
                    ].slice(0, 5)}
                    maxRecipes={2}
                  />
                )}

                {/* Farm Recommendations */}
                <FarmRecommendationsWidget
                  currentFarmId={listing.id}
                  allFarms={listings}
                  maxRecommendations={3}
                />

                {/* Similar Listings */}
                {similarListings.length > 0 && (
                  <div style={styles.similarCard}>
                    <h3 style={styles.cardTitle}>Similar Listings</h3>
                    <div style={styles.similarList}>
                      {similarListings.map(similar => (
                        <Link
                          key={similar.id}
                          to={`/listing/${similar.slug}`}
                          style={styles.similarItem}
                        >
                          <img
                            src={similar.images?.[0] || 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=100&h=80&fit=crop'}
                            alt={similar.name}
                            style={styles.similarImg}
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=100&h=80&fit=crop';
                            }}
                          />
                          <div style={styles.similarInfo}>
                            <span style={styles.similarName}>{similar.name}</span>
                            <span style={styles.similarRating}>
                              <Star size={12} fill="var(--thm-accent)" color="var(--thm-accent)" />
                              {similar.rating}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterOne />
    </>
  );
};

const styles = {
  errorContainer: {
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--thm-bg-cream)',
  },
  errorContent: {
    textAlign: 'center',
    padding: '40px',
  },
  errorIcon: {
    fontSize: '64px',
    display: 'block',
    marginBottom: '20px',
  },
  errorTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '12px',
  },
  errorText: {
    fontSize: '16px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    marginBottom: '24px',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 24px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
  },
  heroSection: {
    backgroundColor: 'var(--thm-bg-sage)',
    paddingTop: '20px',
  },
  breadcrumbLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    color: 'var(--thm-base)',
    fontFamily: 'var(--thm-font-2)',
    fontWeight: '600',
    textDecoration: 'none',
    marginBottom: '16px',
  },
  imageGallery: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 15px',
  },
  mainImage: {
    position: 'relative',
    borderRadius: '16px 16px 0 0',
    overflow: 'hidden',
    height: '400px',
  },
  mainImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%)',
  },
  listingType: {
    padding: '6px 14px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textTransform: 'uppercase',
  },
  imageActions: {
    display: 'flex',
    gap: '10px',
  },
  actionBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.9)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--thm-black)',
    transition: 'all 0.2s ease',
  },
  actionBtnActive: {
    backgroundColor: 'var(--thm-accent)',
    color: '#fff',
  },
  thumbnailRow: {
    display: 'flex',
    gap: '10px',
    padding: '10px',
    backgroundColor: '#fff',
    borderRadius: '0 0 16px 16px',
  },
  thumbnail: {
    flex: 1,
    height: '80px',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    opacity: 0.6,
    transition: 'opacity 0.2s ease',
  },
  thumbnailActive: {
    opacity: 1,
    boxShadow: '0 0 0 3px var(--thm-base)',
  },
  thumbnailImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mainContent: {
    padding: '40px 0 80px',
    backgroundColor: 'var(--thm-bg-cream)',
  },
  listingHeader: {
    marginBottom: '30px',
  },
  headerMain: {},
  listingName: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '12px',
  },
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
  },
  rating: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  ratingScore: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font-2)',
  },
  reviewCount: {
    fontSize: '14px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textTransform: 'uppercase',
  },
  statusOpen: {
    backgroundColor: 'rgba(45, 90, 39, 0.1)',
    color: 'var(--thm-base)',
  },
  statusClosed: {
    backgroundColor: 'rgba(200, 0, 0, 0.1)',
    color: '#c00',
  },
  location: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '15px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    margin: 0,
  },
  tabsContainer: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '30px',
    overflow: 'hidden',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
  },
  tab: {
    flex: 1,
    padding: '16px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    color: 'var(--thm-gray)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  tabActive: {
    color: 'var(--thm-base)',
    backgroundColor: 'var(--thm-bg-sage)',
  },
  tabContent: {
    padding: '30px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '16px',
  },
  subsectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: 'var(--thm-font)',
    marginBottom: '12px',
  },
  description: {
    fontSize: '15px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    lineHeight: 1.7,
    marginBottom: '30px',
  },
  featuresSection: {
    marginBottom: '30px',
  },
  featuresList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'var(--thm-bg-sage)',
    borderRadius: '8px',
  },
  featureIcon: {
    fontSize: '18px',
  },
  featureName: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font-2)',
  },
  seasonalSection: {
    marginTop: '20px',
  },
  seasonalText: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '15px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
  },
  productsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  productTagLink: {
    textDecoration: 'none',
    display: 'inline-block',
  },
  productTag: {
    padding: '8px 16px',
    backgroundColor: 'var(--thm-bg-sage)',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--thm-base)',
    fontFamily: 'var(--thm-font-2)',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  hoursList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  hoursRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: 'var(--thm-bg-cream)',
    borderRadius: '8px',
  },
  hoursRowToday: {
    backgroundColor: 'var(--thm-bg-sage)',
    border: '1px solid var(--thm-base)',
  },
  dayName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font-2)',
  },
  hoursText: {
    fontSize: '14px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
  },
  noDataText: {
    fontSize: '15px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    fontStyle: 'italic',
  },
  reviewsOverview: {
    marginBottom: '20px',
  },
  reviewsScore: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '30px',
    backgroundColor: 'var(--thm-bg-sage)',
    borderRadius: '12px',
    maxWidth: '200px',
  },
  bigScore: {
    fontSize: '48px',
    fontWeight: '700',
    color: 'var(--thm-base)',
    fontFamily: 'var(--thm-font)',
    lineHeight: 1,
  },
  starsRow: {
    display: 'flex',
    gap: '4px',
    margin: '10px 0',
  },
  totalReviews: {
    fontSize: '14px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
  },
  reviewsMessage: {
    marginTop: '20px',
  },
  reviewsText: {
    fontSize: '15px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    lineHeight: 1.7,
    marginBottom: '16px',
  },
  externalLink: {
    color: 'var(--thm-base)',
    fontWeight: '600',
    textDecoration: 'underline',
  },
  reviewPlatforms: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '20px',
  },
  platformBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: 'var(--thm-bg-sage)',
    color: 'var(--thm-base)',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
    border: '1px solid rgba(45, 90, 39, 0.2)',
    transition: 'all 0.2s ease',
  },
  mapSection: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  mapContainer: {
    height: '300px',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '20px',
  },
  directionsBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
  },
  sidebar: {
    position: 'sticky',
    top: '20px',
  },
  contactCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    fontSize: '15px',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  contactIcon: {
    color: 'var(--thm-base)',
    flexShrink: 0,
  },
  hoursCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  todayHours: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--thm-base)',
    fontFamily: 'var(--thm-font-2)',
    margin: 0,
  },
  similarCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  similarList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  similarItem: {
    display: 'flex',
    gap: '12px',
    padding: '10px',
    backgroundColor: 'var(--thm-bg-cream)',
    borderRadius: '10px',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
  },
  similarImg: {
    width: '70px',
    height: '60px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  similarInfo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '4px',
  },
  similarName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font-2)',
  },
  similarRating: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
  },
};

export default ListingsDetails;
